import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { HttpService } from './http.service';

export interface JobActionResult {
  status: boolean;
  message: string;
  stripe_error?: boolean;
  data?: any;
}

@Injectable({ providedIn: 'root' })
export class JobActionsService {
  private http = inject(HttpService);

  // All job actions hit the Node host (environment.API_URL via HttpService); the
  // TokenInterceptor attaches the Node `newtoken`. Node returns proper HTTP status
  // codes, so normalize non-2xx back to the {status,message,stripe_error?} shape
  // that PHP always returned (HTTP 200) and every caller's success handler expects.
  private send(jobId: number | string, action: string, body?: any): Observable<JobActionResult> {
    return this.http.post(`jobs/${jobId}/${action}`, body ?? {}).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err?.error && typeof err.error === 'object' && typeof err.error.status === 'boolean') {
          return of(err.error as JobActionResult);
        }
        // No JSON body from the API: the request never got there (offline,
        // timeout, gateway) — name that, and keep the HTTP code for support.
        const message =
          err?.status === 0
            ? "Couldn't reach AutoMonkey. Check your connection and try again."
            : `Something went wrong on our side (error ${err?.status || 'unknown'}). Please try again in a moment or contact support.`;
        return of({ status: false, message } as JobActionResult);
      }),
    );
  }

  acceptJob(jobId: number | string) { return this.send(jobId, 'accept'); }
  rejectJob(jobId: number | string) { return this.send(jobId, 'reject'); }
  cancelOrder(jobId: number | string) { return this.send(jobId, 'cancel'); }
  enRoute(jobId: number | string) { return this.send(jobId, 'en-route'); }
  startWork(jobId: number | string) { return this.send(jobId, 'start-work'); }
  pickupParts(jobId: number | string) { return this.send(jobId, 'pickup-parts'); }
  pauseJob(jobId: number | string) { return this.send(jobId, 'pause'); }

  scheduleRepair(jobId: number | string, opts: { appointmentId?: any; mechanicId?: any } = {}) {
    return this.send(jobId, 'schedule', opts);
  }

  acceptEstimate(
    jobId: number | string,
    opts: { signature?: string; mechanic_id?: any; eventId?: any } = {}
  ) {
    return this.send(jobId, 'accept-estimate', opts);
  }

  declineEstimate(jobId: number | string) { return this.send(jobId, 'decline-estimate'); }

  complete(jobId: number | string, opts: { odometerCount?: string | number; photo?: Blob }) {
    const fd = new FormData();
    if (opts.odometerCount != null && String(opts.odometerCount).length > 0) {
      fd.append('odometer_count', String(opts.odometerCount));
    }
    if (opts.photo) fd.append('repair_complete_image', opts.photo);
    return this.send(jobId, 'complete', fd);
  }
}
